import { c as _c } from "react/compiler-runtime";
import { setIn } from 'formik';
import { type ComponentProps, useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { APIDestination, DeliveryGroupRateLimitPeriod } from '../../../../../../../../typings/Destination.interface';
import Button from '../../../../common/base/Button';
import Icon from '../../../../common/base/Icon';
import Tabs from '../../../../common/base/Tabs';
import Text from '../../../../common/base/Text';
import { useDialog } from '../../../../common/Dialog';
import Checkbox from '../../../../common/Form/Fields/Checkbox';
import { Div } from '../../../../common/helpers/StyledUtils';
import { useToasts } from '../../../../common/Toast';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { MetricOptions } from '../../../../common/metrics/metric-definitions';
import ChartInteractionGroup from '../../../../common/Chart/ChartInteractionGroup';
import Search from '../../../../common/Search';
import { StyledViewSplitPaneContent, StyledViewSplitPaneFooter, StyledViewSplitPaneLayout, StyledViewSplitPaneSidebar } from '../../../../layouts/ViewLayout';
import { DashboardContext } from '../../DashboardContext';
import MetricChart from '../../Metrics/MetricChart';
import MetricDatePicker, { useDateRange } from '../../Metrics/MetricDatePicker';
import { useRefreshKey } from '../../Metrics/RefreshKeyContext';
import { TableData, useMetric } from '../../Metrics/useMetric';
import { DeliveryGroupOverrideFormValue, DeliveryGroupOverridesTable, MAX_DELIVERY_GROUP_LENGTH, getDeliveryRateDefaultFromDestination } from './DeliveryGroupsSettings';
import { getDestinationDeliveryPolicy, mapPolicyGroupsToDeliveryGroupsConfig } from '../Forms/destination_configuration';
import { sub } from 'date-fns';
import { formatNumberShorthand } from '../../../../../utils';
const DEFAULT_VISIBLE_GROUP_COUNT = 5;
const DELIVERY_GROUP_METRIC_GRID_GAP = 24;
const DELIVERY_GROUP_METRICS_BREAKPOINT = 1400;
const UNKNOWN_DELIVERY_GROUP_KEY = '';
const StyledDeliveryGroupSidebar = styled(StyledViewSplitPaneSidebar)(() => css`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0;
  `);
const StyledDeliveryGroupControls = styled(Div)(({
  theme
}) => css`
    width: 100%;
    box-sizing: border-box;
    flex-shrink: 0;
    padding: ${theme.spacing(6)} ${theme.spacing(6)} 0;
    border-bottom: ${theme.border};
    margin-bottom: ${theme.spacing(2)};
  `);
const StyledDeliveryGroupList = styled(Div)(({
  theme
}) => css`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    box-sizing: border-box;
    padding: 0 ${theme.spacing(4)};
  `);
const StyledDeliveryGroupFooter = styled(StyledViewSplitPaneFooter)(() => css`
    position: static;
    flex-shrink: 0;
  `);
const StyledDeliveryGroupMetricsContent = styled(StyledViewSplitPaneContent)(() => css`
    height: var(--view-split-pane-height);
    max-height: var(--view-split-pane-height);
    overflow: hidden;
    display: flex;
    flex-direction: column;

    @media (max-width: ${DELIVERY_GROUP_METRICS_BREAKPOINT}px) {
      height: auto;
      max-height: none;
      overflow: visible;
    }
  `);
const StyledDeliveryGroupMetricsGrid = styled(Div)(({
  theme
}) => css`
    flex: 1 1 auto;
    min-height: 0;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-rows: repeat(2, minmax(0, 1fr));
    gap: ${theme.pxToRem(DELIVERY_GROUP_METRIC_GRID_GAP)};

    @media (max-width: ${DELIVERY_GROUP_METRICS_BREAKPOINT}px) {
      grid-template-columns: minmax(0, 1fr);
      grid-template-rows: none;
    }
  `);
const StyledDeliveryGroupMetricCell = styled.div`
  height: 100%;
  min-height: 0;

  @media (max-width: ${DELIVERY_GROUP_METRICS_BREAKPOINT}px) {
    height: auto;
  }
`;
const DeliveryGroupMetricChart = props => {
  const $ = _c(5);
  const cell_ref = useRef(null);
  const [height, setHeight] = useState();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const updateHeight = () => {
        if (window.innerWidth <= DELIVERY_GROUP_METRICS_BREAKPOINT) {
          setHeight(undefined);
          return;
        }
        const next_height = cell_ref.current?.getBoundingClientRect().height;
        if (next_height) {
          setHeight(Math.floor(next_height));
        }
      };
      updateHeight();
      const resize_observer = new ResizeObserver(updateHeight);
      if (cell_ref.current) {
        resize_observer.observe(cell_ref.current);
      }
      window.addEventListener("resize", updateHeight);
      return () => {
        resize_observer.disconnect();
        window.removeEventListener("resize", updateHeight);
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== height || $[3] !== props) {
    t2 = <StyledDeliveryGroupMetricCell ref={cell_ref}><MetricChart {...props} height={height} /></StyledDeliveryGroupMetricCell>;
    $[2] = height;
    $[3] = props;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
type DeliveryGroup = {
  key: string;
  event_count: number;
  pending_count: number;
};
type DeliveryGroupRateOverrideFormValues = {
  config: {
    delivery_groups: {
      overrides: DeliveryGroupOverrideFormValue[];
    };
  };
};
const validateDeliveryGroupRateOverrides = (values: DeliveryGroupRateOverrideFormValues) => {
  let errors = {};
  values.config.delivery_groups.overrides.forEach((override, index) => {
    if (override.key && override.key.length > MAX_DELIVERY_GROUP_LENGTH) {
      errors = setIn(errors, `config.delivery_groups.overrides.${index}.key`, `Must be ${MAX_DELIVERY_GROUP_LENGTH} characters or less`);
    }
    if (override.key && (!override.rate_limit || Number(override.rate_limit) <= 0)) {
      errors = setIn(errors, `config.delivery_groups.overrides.${index}.rate_limit`, 'Must be greater than 0');
    }
  });
  return errors;
};
const getDeliveryGroupRateLimit = (destination: APIDestination, delivery_group: string): {
  label: string;
  source: 'default' | 'override' | 'none';
} => {
  const delivery_groups = mapPolicyGroupsToDeliveryGroupsConfig(getDestinationDeliveryPolicy(destination)?.groups);
  const override = delivery_groups?.overrides?.[delivery_group];
  const rate = override?.rate_limit || delivery_groups?.rate_limit;
  const rate_period = override?.rate_limit_period || delivery_groups?.rate_limit_period;
  if (!rate || !rate_period) {
    return {
      label: 'No group rate limit',
      source: 'none'
    };
  }
  return {
    label: `${rate.toLocaleString()}/${rate_period}`,
    source: override ? 'override' : 'default'
  };
};
const DeliveryGroupListItem = t0 => {
  const $ = _c(28);
  const {
    destination,
    delivery_group,
    checked,
    onChange
  } = t0;
  let t1;
  if ($[0] !== delivery_group.key || $[1] !== destination) {
    t1 = getDeliveryGroupRateLimit(destination, delivery_group.key);
    $[0] = delivery_group.key;
    $[1] = destination;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const rate_limit = t1;
  const has_backpressure = delivery_group.pending_count > 0;
  const t2 = `delivery_group_${delivery_group.key}`;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      y: 2,
      x: 2
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      direction: "column"
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      overflowWrap: "anywhere",
      whiteSpace: "normal"
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = delivery_group.key || "Unknown";
  let t7;
  if ($[6] !== t6) {
    t7 = <Text mono={true} size="s" style={t5}>{t6}</Text>;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      align: "center",
      gap: 2,
      wrap: true
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t10;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      align: "center",
      gap: 1
    };
    t10 = <Icon icon="rate_limit" />;
    $[9] = t10;
    $[10] = t9;
  } else {
    t10 = $[9];
    t9 = $[10];
  }
  let t11;
  if ($[11] !== rate_limit.label) {
    t11 = <Text size="xs" muted={true} semi={true} flex={t9}>{t10}{rate_limit.label}</Text>;
    $[11] = rate_limit.label;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Text size="xs" muted={true} semi={true}>{"\xB7"}</Text>;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== delivery_group.pending_count || $[15] !== has_backpressure) {
    t13 = has_backpressure ? <Text danger={true} size="xs" semi={true} flex={{
      align: "center",
      gap: 1
    }}>{formatNumberShorthand(delivery_group.pending_count)} pending</Text> : <Text size="xs" muted={true} semi={true}>No backpressure</Text>;
    $[14] = delivery_group.pending_count;
    $[15] = has_backpressure;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t11 || $[18] !== t13) {
    t14 = <Div flex={t8}>{t11}{t12}{t13}</Div>;
    $[17] = t11;
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t14 || $[21] !== t7) {
    t15 = <Div flex={t4}>{t7}{t14}</Div>;
    $[20] = t14;
    $[21] = t7;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== checked || $[24] !== onChange || $[25] !== t15 || $[26] !== t2) {
    t16 = <Checkbox name={t2} checked={checked} highlight={true} rounded={true} onChange={onChange} p={t3} label={t15} />;
    $[23] = checked;
    $[24] = onChange;
    $[25] = t15;
    $[26] = t2;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  return t16;
};
export const DestinationDeliveryGroups = t0 => {
  const $ = _c(151);
  const {
    destination,
    mutate,
    allowed_delivery_groups
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    is_degraded_banner_active,
    team
  } = useContext(DashboardContext);
  const {
    refresh_key,
    updateRefreshKey
  } = useRefreshKey();
  const {
    addToast
  } = useToasts();
  const showDialog = useDialog();
  const [date_range, onDateRangeChanged] = useDateRange();
  const [active_group_tab, setActiveGroupTab] = useState("all");
  const [group_search, setGroupSearch] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [selected_delivery_groups, setSelectedDeliveryGroups] = useState(t1);
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[1] !== destination.id || $[2] !== refresh_key) {
    const now = new Date(refresh_key);
    t5 = useMetric;
    t6 = "table";
    t7 = "events_pending_count";
    t8 = refresh_key;
    let t9;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = ["delivery_group"];
      $[10] = t9;
    } else {
      t9 = $[10];
    }
    let t10;
    if ($[11] !== destination.id) {
      t10 = {
        dimensions: t9,
        filters: {
          destination_id: [destination.id]
        }
      };
      $[11] = destination.id;
      $[12] = t10;
    } else {
      t10 = $[12];
    }
    t4 = t10 satisfies MetricOptions;
    t2 = now.toISOString();
    t3 = sub(now, {
      minutes: 1
    }).toISOString();
    $[1] = destination.id;
    $[2] = refresh_key;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
  } else {
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
    t8 = $[9];
  }
  let t9;
  if ($[13] !== t2 || $[14] !== t3) {
    t9 = {
      max: t2,
      min: t3
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t4 || $[17] !== t9) {
    t10 = {
      options: t4,
      date_range: t9
    };
    $[16] = t4;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const {
    data: current_pending_counts
  } = t5(t6, t7, t8, t10);
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = ["delivery_group"];
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== destination.id) {
    t12 = {
      dimensions: t11,
      filters: {
        destination_id: [destination.id]
      }
    };
    $[20] = destination.id;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = t12 satisfies MetricOptions;
  let t14;
  if ($[22] !== date_range || $[23] !== t13) {
    t14 = {
      options: t13,
      date_range
    };
    $[22] = date_range;
    $[23] = t13;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const {
    data: group_event_counts
  } = useMetric("table", "events_count", refresh_key, t14);
  let t15;
  if ($[25] !== allowed_delivery_groups || $[26] !== current_pending_counts || $[27] !== group_event_counts) {
    const groups_by_key = new Map();
    (allowed_delivery_groups || []).forEach(delivery_group => {
      const delivery_group_key = delivery_group ?? UNKNOWN_DELIVERY_GROUP_KEY;
      groups_by_key.set(delivery_group_key, {
        key: delivery_group_key,
        event_count: 0,
        pending_count: 0
      });
    });
    (group_event_counts || []).forEach(t16 => {
      const {
        resource_id,
        value
      } = t16;
      const delivery_group_key_0 = resource_id ?? UNKNOWN_DELIVERY_GROUP_KEY;
      groups_by_key.set(delivery_group_key_0, {
        key: delivery_group_key_0,
        event_count: value,
        pending_count: 0
      });
    });
    (current_pending_counts || []).forEach(t17 => {
      const {
        resource_id: resource_id_0,
        value: value_0
      } = t17;
      const delivery_group_key_1 = resource_id_0 ?? UNKNOWN_DELIVERY_GROUP_KEY;
      const delivery_group_0 = groups_by_key.get(delivery_group_key_1);
      groups_by_key.set(delivery_group_key_1, {
        key: delivery_group_key_1,
        event_count: delivery_group_0?.event_count || 0,
        pending_count: value_0
      });
    });
    const allowed_group_keys = allowed_delivery_groups ? new Set(allowed_delivery_groups.map(_temp)) : null;
    t15 = Array.from(groups_by_key.values()).filter(delivery_group_2 => !allowed_group_keys || allowed_group_keys.has(delivery_group_2.key)).sort(_temp2);
    $[25] = allowed_delivery_groups;
    $[26] = current_pending_counts;
    $[27] = group_event_counts;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const delivery_groups = t15;
  let t16;
  if ($[29] !== delivery_groups) {
    t16 = delivery_groups.filter(_temp3);
    $[29] = delivery_groups;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const backpressure_delivery_groups = t16;
  let t17;
  if ($[31] !== active_group_tab || $[32] !== backpressure_delivery_groups || $[33] !== delivery_groups || $[34] !== group_search) {
    bb0: {
      const base_groups = active_group_tab === "backpressure" ? backpressure_delivery_groups : delivery_groups;
      const search = group_search.trim().toLowerCase();
      if (!search) {
        t17 = base_groups;
        break bb0;
      }
      t17 = base_groups.filter(t18 => {
        const {
          key
        } = t18;
        return (key || "Unknown").toLowerCase().includes(search);
      });
    }
    $[31] = active_group_tab;
    $[32] = backpressure_delivery_groups;
    $[33] = delivery_groups;
    $[34] = group_search;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const visible_delivery_groups = t17;
  let t18;
  if ($[36] !== visible_delivery_groups) {
    t18 = visible_delivery_groups.slice(0, DEFAULT_VISIBLE_GROUP_COUNT).map(_temp4);
    $[36] = visible_delivery_groups;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const default_delivery_groups = t18;
  const metric_delivery_groups = selected_delivery_groups.length ? selected_delivery_groups : default_delivery_groups;
  let t19;
  if ($[38] !== metric_delivery_groups) {
    t19 = metric_delivery_groups.length === 1 && metric_delivery_groups[0] === UNKNOWN_DELIVERY_GROUP_KEY ? null : metric_delivery_groups.map(_temp5);
    $[38] = metric_delivery_groups;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  const delivery_group_filter = t19;
  let t20;
  if ($[40] !== destination.id) {
    t20 = [destination.id];
    $[40] = destination.id;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== delivery_group_filter || $[43] !== metric_delivery_groups.length) {
    t21 = metric_delivery_groups.length > 0 ? {
      delivery_group: delivery_group_filter
    } : {};
    $[42] = delivery_group_filter;
    $[43] = metric_delivery_groups.length;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[45] !== t20 || $[46] !== t21) {
    t22 = {
      destination_id: t20,
      ...t21
    };
    $[45] = t20;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  const metric_filters = t22;
  const is_loading_groups = current_pending_counts === undefined || group_event_counts === undefined;
  let t23;
  let t24;
  if ($[48] !== delivery_groups || $[49] !== is_loading_groups || $[50] !== selected_delivery_groups.length) {
    t23 = () => {
      if (is_loading_groups || selected_delivery_groups.length === 0) {
        return;
      }
      const visible_group_values = new Set(delivery_groups.map(_temp6));
      setSelectedDeliveryGroups(current_groups => {
        const next_groups = current_groups.filter(key_3 => visible_group_values.has(key_3));
        return next_groups.length === current_groups.length ? current_groups : next_groups;
      });
    };
    t24 = [delivery_groups, is_loading_groups, selected_delivery_groups.length];
    $[48] = delivery_groups;
    $[49] = is_loading_groups;
    $[50] = selected_delivery_groups.length;
    $[51] = t23;
    $[52] = t24;
  } else {
    t23 = $[51];
    t24 = $[52];
  }
  useEffect(t23, t24);
  let t25;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = delivery_group_3 => {
      setSelectedDeliveryGroups(current_groups_0 => current_groups_0.includes(delivery_group_3) ? current_groups_0.filter(key_4 => key_4 !== delivery_group_3) : [...current_groups_0, delivery_group_3]);
    };
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  const toggleDeliveryGroup = t25;
  let t26;
  if ($[54] !== HookdeckAPI || $[55] !== addToast || $[56] !== destination || $[57] !== mutate || $[58] !== selected_delivery_groups || $[59] !== showDialog || $[60] !== team?.max_events_per_second) {
    t26 = () => {
      const delivery_policy = getDestinationDeliveryPolicy(destination);
      const delivery_groups_0 = mapPolicyGroupsToDeliveryGroupsConfig(delivery_policy?.groups);
      if (!delivery_groups_0) {
        addToast("error", "Delivery groups are not configured");
        return;
      }
      const destination_default_delivery_rate = getDeliveryRateDefaultFromDestination(destination, team?.max_events_per_second);
      const default_group_delivery_rate = {
        rate_limit: delivery_groups_0.rate_limit ? String(delivery_groups_0.rate_limit) : destination_default_delivery_rate.rate_limit,
        rate_limit_period: delivery_groups_0.rate_limit_period || destination_default_delivery_rate.rate_limit_period
      };
      const selected_overrides = selected_delivery_groups.map(key_5 => {
        const override = delivery_groups_0.overrides?.[key_5];
        return {
          key: key_5,
          rate_limit: override?.rate_limit ? String(override.rate_limit) : default_group_delivery_rate.rate_limit,
          rate_limit_period: override?.rate_limit_period || default_group_delivery_rate.rate_limit_period
        };
      });
      showDialog(async values => {
        const selected_group_set = new Set(selected_delivery_groups);
        const existing_overrides = Object.fromEntries(Object.entries(delivery_groups_0.overrides || {}).filter(t27 => {
          const [key_6] = t27;
          return !selected_group_set.has(key_6);
        }));
        const updated_overrides = Object.fromEntries(values.config.delivery_groups.overrides.filter(_temp7).map(_temp8));
        const overrides = {
          ...existing_overrides,
          ...updated_overrides
        };
        return HookdeckAPI.destinations.update(destination.id, {
          type: destination.type,
          config: {
            ...destination.config,
            delivery_policy: {
              ...(delivery_policy || {}),
              groups: {
                key: delivery_groups_0.key,
                rate: delivery_groups_0.rate_limit,
                rate_period: delivery_groups_0.rate_limit_period,
                overrides: Object.keys(overrides).length > 0 ? Object.fromEntries(Object.entries(overrides).map(_temp9)) : null
              }
            }
          }
        }).then(updated_destination => {
          mutate({
            ...destination,
            ...updated_destination
          });
          setSelectedDeliveryGroups([]);
          addToast("success", "Delivery group rates updated");
        }).catch(e => {
          addToast("error", `Failed to update delivery group rates${e.response?.data[0] ? `: ${e.response.data[0]}` : ""}`);
          throw e;
        });
      }, _temp10, {
        title: "Edit max delivery rate",
        message: <Div flex={{
          direction: "column",
          gap: 4
        }}><Text>Modify max delivery rates of your selected delivery groups.</Text></Div>,
        cancel_label: "Cancel",
        submit_label: "Update",
        form_props: {
          initial_values: {
            config: {
              delivery_groups: {
                overrides: selected_overrides
              }
            }
          },
          validate: _temp11,
          Fields: () => <Div m={{
            t: 4
          }}><DeliveryGroupOverridesTable default_rate={default_group_delivery_rate.rate_limit} default_rate_period={default_group_delivery_rate.rate_limit_period} show_heading={false} /></Div>
        }
      });
    };
    $[54] = HookdeckAPI;
    $[55] = addToast;
    $[56] = destination;
    $[57] = mutate;
    $[58] = selected_delivery_groups;
    $[59] = showDialog;
    $[60] = team?.max_events_per_second;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  const openDeliveryRateDialog = t26;
  let t27;
  let t28;
  let t29;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = {
      align: "center",
      justify: "space-between",
      gap: 3
    };
    t28 = {
      t: -1,
      b: 4
    };
    t29 = <Text as="h2" size="l" bold={true} m={0}>Delivery groups</Text>;
    $[62] = t27;
    $[63] = t28;
    $[64] = t29;
  } else {
    t27 = $[62];
    t28 = $[63];
    t29 = $[64];
  }
  let t30;
  if ($[65] !== onDateRangeChanged) {
    t30 = date_range_0 => {
      onDateRangeChanged(date_range_0);
    };
    $[65] = onDateRangeChanged;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== date_range || $[68] !== t30) {
    t31 = <Div flex={t27} m={t28}>{t29}<MetricDatePicker portal={true} date_range={date_range} onDateRangeChanged={t30} /></Div>;
    $[67] = date_range;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = {
      b: 4
    };
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== group_search) {
    t33 = <Search small={true} placeholder="Filter by group name" value={group_search} onChange={setGroupSearch} m={t32} />;
    $[71] = group_search;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== active_group_tab || $[74] !== allowed_delivery_groups || $[75] !== backpressure_delivery_groups || $[76] !== delivery_groups.length) {
    t34 = !allowed_delivery_groups && <Tabs compact={true} border={false} active_tab={active_group_tab} onTabSelected={tab => setActiveGroupTab(tab as 'all' | 'backpressure')} tabs={[{
      key: "all",
      label: "All",
      badge_label: delivery_groups.length,
      badge_theme: "muted"
    }, {
      key: "backpressure",
      label: "With backpressure",
      badge_label: backpressure_delivery_groups.length,
      badge_theme: backpressure_delivery_groups.length > 0 ? "danger" : "muted"
    }]} m={0} />;
    $[73] = active_group_tab;
    $[74] = allowed_delivery_groups;
    $[75] = backpressure_delivery_groups;
    $[76] = delivery_groups.length;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== t31 || $[79] !== t33 || $[80] !== t34) {
    t35 = <StyledDeliveryGroupControls>{t31}{t33}{t34}</StyledDeliveryGroupControls>;
    $[78] = t31;
    $[79] = t33;
    $[80] = t34;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] !== allowed_delivery_groups || $[83] !== destination || $[84] !== is_loading_groups || $[85] !== selected_delivery_groups || $[86] !== visible_delivery_groups) {
    t36 = <StyledDeliveryGroupList>{is_loading_groups ? <Div flex={{
        justify: "center",
        align: "center",
        gap: 2
      }}><Icon icon="loading" muted={true} /></Div> : visible_delivery_groups.length > 0 ? <Div flex={{
        direction: "column",
        gap: 1
      }}>{visible_delivery_groups.map(delivery_group_4 => <DeliveryGroupListItem key={delivery_group_4.key || "unknown_delivery_group"} destination={destination} delivery_group={delivery_group_4} checked={selected_delivery_groups.includes(delivery_group_4.key)} onChange={() => toggleDeliveryGroup(delivery_group_4.key)} />)}</Div> : <Div h={100} w={100} p={{
        x: 2
      }} flex={{
        justify: "center",
        align: "center"
      }}><Text neutral={true} semi={true} center={true} m={0}>{allowed_delivery_groups ? "No delivery groups were recorded for this issue." : "No delivery groups for this time period."}</Text></Div>}</StyledDeliveryGroupList>;
    $[82] = allowed_delivery_groups;
    $[83] = destination;
    $[84] = is_loading_groups;
    $[85] = selected_delivery_groups;
    $[86] = visible_delivery_groups;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = {
      gap: 3
    };
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  const t38 = selected_delivery_groups.length === 0;
  let t39;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = () => {
      setSelectedDeliveryGroups([]);
    };
    $[89] = t39;
  } else {
    t39 = $[89];
  }
  let t40;
  if ($[90] !== t38) {
    t40 = <Button outline={true} disabled={t38} onClick={t39}>Clear selections</Button>;
    $[90] = t38;
    $[91] = t40;
  } else {
    t40 = $[91];
  }
  let t41;
  if ($[92] !== destination || $[93] !== selected_delivery_groups) {
    t41 = selected_delivery_groups.every(_temp12) || !getDestinationDeliveryPolicy(destination)?.groups;
    $[92] = destination;
    $[93] = selected_delivery_groups;
    $[94] = t41;
  } else {
    t41 = $[94];
  }
  let t42;
  if ($[95] !== openDeliveryRateDialog || $[96] !== t41) {
    t42 = <Button outline={true} icon="rate_limit" disabled={t41} onClick={openDeliveryRateDialog}>Edit delivery rate</Button>;
    $[95] = openDeliveryRateDialog;
    $[96] = t41;
    $[97] = t42;
  } else {
    t42 = $[97];
  }
  let t43;
  if ($[98] !== t40 || $[99] !== t42) {
    t43 = <StyledDeliveryGroupFooter><Div flex={t37}>{t40}{t42}</Div></StyledDeliveryGroupFooter>;
    $[98] = t40;
    $[99] = t42;
    $[100] = t43;
  } else {
    t43 = $[100];
  }
  let t44;
  if ($[101] !== t35 || $[102] !== t36 || $[103] !== t43) {
    t44 = <StyledDeliveryGroupSidebar>{t35}{t36}{t43}</StyledDeliveryGroupSidebar>;
    $[101] = t35;
    $[102] = t36;
    $[103] = t43;
    $[104] = t44;
  } else {
    t44 = $[104];
  }
  let t45;
  let t46;
  let t47;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = {
      align: "center",
      justify: "space-between",
      gap: 3
    };
    t46 = {
      t: -1,
      b: 4
    };
    t47 = <Text as="h2" size="l" bold={true} m={0}>Delivery group metrics</Text>;
    $[105] = t45;
    $[106] = t46;
    $[107] = t47;
  } else {
    t45 = $[105];
    t46 = $[106];
    t47 = $[107];
  }
  let t48;
  if ($[108] !== updateRefreshKey) {
    t48 = <Div flex={t45} m={t46}>{t47}<Button outline={true} icon="reload" aria-label="Reload delivery group metrics" title="Reload delivery group metrics" onClick={() => updateRefreshKey()}>Refresh</Button></Div>;
    $[108] = updateRefreshKey;
    $[109] = t48;
  } else {
    t48 = $[109];
  }
  let t49;
  if ($[110] !== onDateRangeChanged) {
    t49 = range => onDateRangeChanged({
      relative: undefined,
      min: range.min,
      max: range.max
    });
    $[110] = onDateRangeChanged;
    $[111] = t49;
  } else {
    t49 = $[111];
  }
  let t50;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = ["delivery_group"];
    $[112] = t50;
  } else {
    t50 = $[112];
  }
  let t51;
  if ($[113] !== metric_filters) {
    t51 = {
      dimensions: t50,
      filters: metric_filters
    };
    $[113] = metric_filters;
    $[114] = t51;
  } else {
    t51 = $[114];
  }
  let t52;
  if ($[115] !== date_range || $[116] !== t51) {
    t52 = <DeliveryGroupMetricChart force_legend={true} chart_type="line" metric="events_rate" configs={{
      date_range,
      options: t51
    }} />;
    $[115] = date_range;
    $[116] = t51;
    $[117] = t52;
  } else {
    t52 = $[117];
  }
  let t53;
  if ($[118] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = ["delivery_group"];
    $[118] = t53;
  } else {
    t53 = $[118];
  }
  let t54;
  if ($[119] !== metric_filters) {
    t54 = {
      dimensions: t53,
      filters: metric_filters
    };
    $[119] = metric_filters;
    $[120] = t54;
  } else {
    t54 = $[120];
  }
  let t55;
  if ($[121] !== date_range || $[122] !== t54) {
    t55 = <DeliveryGroupMetricChart force_legend={true} chart_type="line" title="Delivered attempts" metric="attempts_delivered_rate" configs={{
      date_range,
      options: t54
    }} />;
    $[121] = date_range;
    $[122] = t54;
    $[123] = t55;
  } else {
    t55 = $[123];
  }
  let t56;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = ["delivery_group"];
    $[124] = t56;
  } else {
    t56 = $[124];
  }
  let t57;
  if ($[125] !== metric_filters) {
    t57 = {
      dimensions: t56,
      filters: metric_filters
    };
    $[125] = metric_filters;
    $[126] = t57;
  } else {
    t57 = $[126];
  }
  let t58;
  if ($[127] !== date_range || $[128] !== t57) {
    t58 = <DeliveryGroupMetricChart force_legend={true} chart_type="line" metric="events_pending_count" configs={{
      date_range,
      options: t57
    }} />;
    $[127] = date_range;
    $[128] = t57;
    $[129] = t58;
  } else {
    t58 = $[129];
  }
  let t59;
  if ($[130] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = ["delivery_group"];
    $[130] = t59;
  } else {
    t59 = $[130];
  }
  let t60;
  if ($[131] !== metric_filters) {
    t60 = {
      dimensions: t59,
      filters: metric_filters
    };
    $[131] = metric_filters;
    $[132] = t60;
  } else {
    t60 = $[132];
  }
  let t61;
  if ($[133] !== date_range || $[134] !== t60) {
    t61 = <DeliveryGroupMetricChart force_legend={true} chart_type="line" metric="events_oldest_pending" configs={{
      date_range,
      options: t60
    }} />;
    $[133] = date_range;
    $[134] = t60;
    $[135] = t61;
  } else {
    t61 = $[135];
  }
  let t62;
  if ($[136] !== t52 || $[137] !== t55 || $[138] !== t58 || $[139] !== t61) {
    t62 = <StyledDeliveryGroupMetricsGrid>{t52}{t55}{t58}{t61}</StyledDeliveryGroupMetricsGrid>;
    $[136] = t52;
    $[137] = t55;
    $[138] = t58;
    $[139] = t61;
    $[140] = t62;
  } else {
    t62 = $[140];
  }
  let t63;
  if ($[141] !== t49 || $[142] !== t62) {
    t63 = <ChartInteractionGroup onTimeRangeSelected={t49}>{t62}</ChartInteractionGroup>;
    $[141] = t49;
    $[142] = t62;
    $[143] = t63;
  } else {
    t63 = $[143];
  }
  let t64;
  if ($[144] !== t48 || $[145] !== t63) {
    t64 = <StyledDeliveryGroupMetricsContent>{t48}{t63}</StyledDeliveryGroupMetricsContent>;
    $[144] = t48;
    $[145] = t63;
    $[146] = t64;
  } else {
    t64 = $[146];
  }
  let t65;
  if ($[147] !== is_degraded_banner_active || $[148] !== t44 || $[149] !== t64) {
    t65 = <StyledViewSplitPaneLayout banner_active={is_degraded_banner_active}>{t44}{t64}</StyledViewSplitPaneLayout>;
    $[147] = is_degraded_banner_active;
    $[148] = t44;
    $[149] = t64;
    $[150] = t65;
  } else {
    t65 = $[150];
  }
  return t65;
};
function _temp(delivery_group_1) {
  return delivery_group_1 ?? UNKNOWN_DELIVERY_GROUP_KEY;
}
function _temp2(a, b) {
  if (b.pending_count !== a.pending_count) {
    return b.pending_count - a.pending_count;
  }
  return b.event_count - a.event_count;
}
function _temp3(t0) {
  const {
    pending_count
  } = t0;
  return pending_count > 0;
}
function _temp4(t0) {
  const {
    key: key_0
  } = t0;
  return key_0;
}
function _temp5(key_1) {
  return key_1 === UNKNOWN_DELIVERY_GROUP_KEY ? null : key_1;
}
function _temp6(t0) {
  const {
    key: key_2
  } = t0;
  return key_2;
}
function _temp7(override_0) {
  return override_0.key && override_0.rate_limit && override_0.rate_limit_period;
}
function _temp8(override_1) {
  return [override_1.key, {
    rate_limit: Number(override_1.rate_limit),
    rate_limit_period: override_1.rate_limit_period as DeliveryGroupRateLimitPeriod
  }];
}
function _temp9(t0) {
  const [key_7, override_2] = t0;
  return [key_7, {
    rate: override_2.rate_limit,
    rate_period: override_2.rate_limit_period
  }];
}
function _temp10() {
  return null;
}
function _temp11(values_0) {
  return validateDeliveryGroupRateOverrides(values_0 as DeliveryGroupRateOverrideFormValues);
}
function _temp12(delivery_group_5) {
  return delivery_group_5 === UNKNOWN_DELIVERY_GROUP_KEY;
}